import React from "react";
import {
  Activity,
  Aperture,
  Brain,
  Camera,
  CheckCircle2,
  Crosshair,
  Film,
  Grid3X3,
  Layers,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";
import { Section, DemoTag, TipBox } from "./HelpComponents";

const lessonCards = [
  {
    id: 300,
    title: "电影镜头语言",
    icon: Camera,
    enhancement: false,
    summary: "用景别、机位、镜头运动和轴线关系让画面具备导演调度，而不是只写剧情摘要。",
    rules: [
      "先写观众要看到什么信息，再决定远景、中景、近景或特写。",
      "同一场对话要保持镜头轴线，正反打要写清楚谁看谁、人物在画面左右或前后。",
      "镜头运动要有目的：慢推给压力，侧后跟移给追逐，低机位给压迫，空镜承接给停顿。",
      "不要连续堆同一种景别。远景建立空间，中景承载动作，近景抓表演，特写抓证据。",
    ],
    example: "门框压住画面左侧，母亲站在玄关阴影里盯着餐桌；镜头从桌上的冷饭慢慢推到她攥紧的手。",
  },
  {
    id: 301,
    title: "动作/打斗分镜",
    icon: Crosshair,
    enhancement: true,
    mode: "动作打斗",
    summary: "动作不是一句“打起来”。要拆出起势、接触、冲击、余波，让视频模型能连续生成。",
    rules: [
      "复杂动作拆成 2-4 个连续 shots：起势、闪避/接触、撞击、重心恢复。",
      "写清发力点、重心方向、脚步变化、身体姿态和对手反应。",
      "打击点要有暂停或反应镜头，否则画面会变成无意义高速运动。",
      "速度线感、尘土、衣料拉扯、镜头震动可以写，但不要生成漫画拟声字。",
    ],
    example: "男人左脚后撤蓄力，肩背压低；对方拳头擦过他脸侧，他顺势拧腰反击，镜头短震后停在对方踉跄后退的脚步。",
  },
  {
    id: 302,
    title: "特效/VFX镜头",
    icon: Sparkles,
    enhancement: true,
    mode: "特效镜头",
    summary: "特效要有来源、路径、尺度和环境互动，不能只写“炫酷光效”。",
    rules: [
      "写清特效从哪里出现、如何移动、覆盖多大范围、最后如何消散。",
      "让特效作用到环境：照亮脸部、投下影子、反射在玻璃、卷起尘土或水雾。",
      "同一个 shot 只放一个主特效焦点，复杂特效按蓄势、爆发、余波拆开。",
      "除非剧情明确需要，不要生成可读文字、符号、法阵文字或屏幕大字。",
    ],
    example: "掌心蓝白光团贴着指缝聚拢，冷光映亮她的下颌；玻璃柜面出现一条细长反光，随后空气里的尘埃被光线卷起。",
  },
  {
    id: 303,
    title: "情绪/微表演",
    icon: Brain,
    enhancement: true,
    mode: "情绪微反应",
    summary: "人物要像真人。用“人物 + 正在做的事 + 下意识反应 + 情绪落点”替代空泛表情。",
    rules: [
      "不要只写“震惊、愤怒、害怕”。写他正在做什么，以及身体哪里先泄露情绪。",
      "可用动作：停手、吞咽、抓紧杯沿、避开视线、二次确认、呼吸变浅、手指发紧。",
      "微反应要符合处境。上电梯会按按钮并确认楼层，签字会停笔，听坏消息会先僵住。",
      "情绪落点要落在画面结果上：手停在按钮上、杯子悬在半空、门缝光切开脸。",
    ],
    example: "她伸手去按电梯键，指尖停在 18 层前迟疑半秒；屏幕冷光映到眼底，她又抬眼确认了一遍楼层。",
  },
  {
    id: 304,
    title: "多镜头连续性",
    icon: Layers,
    enhancement: false,
    summary: "连续性解决的是观众是否能看懂人物位置和时间空间，不是单镜头好不好看。",
    rules: [
      "同一场景内要保持人物左右、前后、坐站状态、手中物和光线基调一致。",
      "切换正反打时要说明视线方向和镜头轴线，避免越轴造成空间错乱。",
      "转场必须作为 segment 边界，不要把旧场景、新场景塞进同一个视频片段。",
      "下一段的开头要能接住上一段的结束站位或视觉落点。",
    ],
    example: "上一镜停在她从桌右侧起身；下一镜从门口低机位接，她仍在画面右侧前景，桌面文件留在左后方。",
  },
  {
    id: 305,
    title: "物理真实感",
    icon: Activity,
    enhancement: true,
    mode: "物理真实感",
    summary: "视频模型容易瞬移和跳动作。提示词要保留起点、路径、接触点和终点。",
    rules: [
      "所有移动都写清从哪里开始、经过哪里、碰到什么、最后停在哪里。",
      "开门、上车、拿物、递物、摔倒、撞击都要有动作准备和动作落点。",
      "道具位置要连续。上一镜手里有文件，下一镜不能无解释变成空手。",
      "夸张动作可以强化速度感，但不要破坏人物比例、空间尺度和材质逻辑。",
    ],
    example: "他从桌角拿起牛皮纸袋，纸袋边缘擦过水杯，杯子轻晃但没倒；他把纸袋压到胸前，后退半步靠住墙。",
  },
  {
    id: 306,
    title: "构图/画面控制",
    icon: Grid3X3,
    enhancement: true,
    mode: "构图画面",
    summary: "构图让信息有层次。明确前景、中景、背景和视觉锚点，画面会更稳定。",
    rules: [
      "每段至少有一个镜头明确前景/中景/背景关系。",
      "可用门框、车窗、桌沿、楼梯、走廊纵深、雨幕、玻璃反光、屏幕光等构图元素。",
      "压迫用低机位、近距离、前景遮挡；疏离用负空间、背影、隔窗；对峙用稳定轴线。",
      "光线写成可执行描述：冷暖反差、逆光轮廓、阴影切脸、局部高光、雾气柔化。",
    ],
    example: "玻璃门反光占据前景，父亲的背影被压在画面右侧；走廊尽头的暖灯只照到女儿半张脸。",
  },
  {
    id: 307,
    title: "提示词优化/RAG增强",
    icon: ShieldCheck,
    enhancement: true,
    mode: "提示词优化",
    summary: "让模型输出前先使用剧本原文、资产清单和当前设置做检索式约束，再检查抽象句、资产乱用、台词来源、切点和禁用元素。",
    rules: [
      "把 sourceText、assetRefs、storyMode、比例、禁用字幕等当作强上下文，不允许绕过这些上下文自由发挥。",
      "检查 visual 是否具体到人物、地点、动作、物件、光影。",
      "检查 assetRefs 只使用资产库已有名称，不发明新角色、场景、道具。",
      "检查 text 必须有说话人或声音来源，不确定就写无。",
      "检查不要输出 Markdown、解释、表格、@图片、图号、字幕、水印、屏幕大字。",
    ],
    example: "坏句：推进冲突。改写：他把手机扣在桌面上，指节压白；她的视线停在黑屏反光里，没有立刻接话。",
  },
];

const HelpPromptEnhancement = ({ theme, expandedSection, toggleSection }) => (
  <div className="space-y-4">
    <div
      className="rounded-2xl border p-5"
      style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DemoTag label="小故事板" theme={theme} />
        <DemoTag label="视频生成" color={theme.colors.info} theme={theme} />
        <DemoTag label="教程内容已内置" color={theme.colors.success} theme={theme} />
      </div>
      <h2 className="text-xl font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>
        分镜提示词增强教程
      </h2>
      <p className="text-sm leading-7 max-w-3xl" style={{ color: "var(--color-text-secondary)" }}>
        这些方法适合给编剧、导演、分镜师和提示词编辑使用。它们不是要求把所有规则塞进每次生成，而是按当前镜头问题选择强化方向：动作不稳就加动作打斗，情绪空就加微反应，画面平就加构图画面。
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="rounded-xl p-4" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
          <Wand2 size={18} style={{ color: theme.colors.accentPrimary }} />
          <div className="mt-2 text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>可组合</div>
          <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>增强项可以多选，但越多越容易约束过重，建议一次选择 1-3 个。</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
          <Film size={18} style={{ color: theme.colors.accentPrimary }} />
          <div className="mt-2 text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>不改剧情</div>
          <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>增强规则只改镜头表达，不允许新增关键证据、人物身份、反转或对白。</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
          <CheckCircle2 size={18} style={{ color: theme.colors.success }} />
          <div className="mt-2 text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>保持格式</div>
          <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>无论选择什么增强，最终仍输出小故事板 JSON，由后端统一拼视频提示词。</p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {lessonCards.map((card) => (
        <button
          key={card.id}
          onClick={() => toggleSection(card.id)}
          className="rounded-xl border p-4 text-left transition-all"
          style={{
            backgroundColor: expandedSection === card.id ? `${theme.colors.accentPrimary}12` : "var(--color-bg-secondary)",
            borderColor: expandedSection === card.id ? theme.colors.accentPrimary : "var(--color-border-primary)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${theme.colors.accentPrimary}18` }}
            >
              <card.icon size={19} style={{ color: theme.colors.accentPrimary }} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{card.title}</h3>
                {card.enhancement && <DemoTag label={`已接入：${card.mode}`} color={theme.colors.success} theme={theme} />}
              </div>
              <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>{card.summary}</p>
            </div>
          </div>
        </button>
      ))}
    </div>

    <div className="space-y-4">
      {lessonCards.map((card) => (
        <Section
          key={card.id}
          id={card.id}
          title={card.title}
          icon={card.icon}
          expanded={expandedSection === card.id}
          onToggle={toggleSection}
          theme={theme}
        >
          <div className="space-y-4">
            <p className="text-sm leading-7" style={{ color: "var(--color-text-secondary)" }}>{card.summary}</p>
            {card.enhancement ? (
              <TipBox theme={theme} type="success">
                这一类已接入小故事板顶部“分镜增强模式”，可以和其他增强项组合使用。
              </TipBox>
            ) : (
              <TipBox theme={theme}>
                这一类更适合作为教程和人工编辑参考，暂不做独立增强项，避免和现有连续性/导演规则重复。
              </TipBox>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {card.rules.map((rule, index) => (
                <div
                  key={rule}
                  className="rounded-lg border p-3"
                  style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border-secondary)" }}
                >
                  <div className="text-xs font-bold mb-1" style={{ color: theme.colors.accentPrimary }}>
                    要点 {index + 1}
                  </div>
                  <p className="text-xs leading-6" style={{ color: "var(--color-text-secondary)" }}>{rule}</p>
                </div>
              ))}
            </div>
            <div
              className="rounded-xl border p-4"
              style={{ backgroundColor: "var(--color-bg-overlay)", borderColor: "var(--color-border-primary)" }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Aperture size={15} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>示例句</span>
              </div>
              <p className="text-sm leading-7" style={{ color: "var(--color-text-secondary)" }}>{card.example}</p>
            </div>
          </div>
        </Section>
      ))}
    </div>

    <div
      className="rounded-2xl border p-5"
      style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw size={18} style={{ color: theme.colors.accentPrimary }} />
        <h3 className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>组合建议</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          ["动作戏", "动作打斗 + 物理真实感 + 构图画面"],
          ["情绪戏", "情绪微反应 + 构图画面 + 提示词自检"],
          ["奇观戏", "特效镜头 + 物理真实感 + 提示词自检"],
        ].map(([title, value]) => (
          <div key={title} className="rounded-xl p-4" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
            <div className="text-sm font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>{title}</div>
            <div className="text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default HelpPromptEnhancement;
